// v6.09 — TALENT LIBRARY (admin): generate a repository of fictional UGC
// creators as casting "character sheets". Controls: male/female/mixed,
// custom prompt influence, quantity → Sonnet writes N detailed portfolio
// prompts → nano-banana-2 t2i 4k 9:16 → Bunny. Grid polls live while a
// batch generates. Not wired into user-facing casting yet (by design).

import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, Users, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface TalentRow {
  id: number;
  sex: string;
  batchId: string;
  prompt: string;
  influence: string | null;
  cardUrl: string | null;
  status: "generating" | "ready" | "failed";
  error: string | null;
  tags: {
    hairColor?: string;
    hairLength?: string;
    hairTexture?: string;
    ageRange?: string;
    complexion?: string;
    build?: string;
    vibe?: string;
  } | null;
  createdAt: string;
}

export default function TalentLibraryPage() {
  const [sex, setSex] = useState<"female" | "male" | "mixed">("female");
  const [quantity, setQuantity] = useState(10);
  const [influence, setInfluence] = useState("");
  // Optimistic batch: DB rows only exist after the prompt engine finishes
  // (~30-60s of Sonnet time), so the grid shows N placeholder tiles from the
  // moment Generate is clicked until the real rows arrive.
  const [pendingBatch, setPendingBatch] = useState<{ batchId: string; count: number; startedAt: number } | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: rows = [], isLoading } = useQuery<TalentRow[]>({
    queryKey: ["/api/admin/talent-library"],
    queryFn: async () => {
      const res = await fetch("/api/admin/talent-library");
      if (!res.ok) throw new Error("Failed to load talent library");
      return res.json();
    },
    // Live grid while a batch renders — including the pre-row window where
    // the prompt engine is still writing and nothing is in the DB yet.
    refetchInterval: (q) => {
      const hasGenerating = (q.state.data ?? []).some((r) => r.status === "generating");
      const pendingActive = pendingBatch !== null && Date.now() - pendingBatch.startedAt < 5 * 60_000;
      return hasGenerating || pendingActive ? 4000 : false;
    },
  });

  // Retire placeholders as the batch's real rows land.
  const pendingRowsArrived = pendingBatch ? rows.filter((r) => r.batchId === pendingBatch.batchId).length : 0;
  const placeholderCount = pendingBatch ? Math.max(0, pendingBatch.count - pendingRowsArrived) : 0;
  useEffect(() => {
    if (pendingBatch && (pendingRowsArrived >= pendingBatch.count || Date.now() - pendingBatch.startedAt > 5 * 60_000)) {
      setPendingBatch(null);
    }
  }, [pendingBatch, pendingRowsArrived]);

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/talent-library/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sex, quantity, influence: influence.trim() || undefined }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Failed to start batch");
      return res.json() as Promise<{ batchId: string }>;
    },
    onSuccess: (data) => {
      setPendingBatch({ batchId: data.batchId, count: quantity, startedAt: Date.now() });
      toast({ title: "Batch started", description: `Generating ${quantity} ${sex} talent — the grid updates live.` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/talent-library"] });
    },
    onError: (e: Error) => toast({ title: "Batch failed to start", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/talent-library/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/talent-library"] }),
  });

  const ready = rows.filter((r) => r.status === "ready").length;
  const generating = rows.filter((r) => r.status === "generating").length + placeholderCount;
  const failed = rows.filter((r) => r.status === "failed").length;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <Users className="w-6 h-6 text-purple-700" />
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Talent Library</h1>
      </div>
      <p className="text-sm text-[#746F66] mb-6">
        Pre-generated fictional UGC creators (nano-banana-2 · 4K · 9:16) — the future casting pool for video clones.
        {rows.length > 0 && (
          <span className="ml-2 text-[#3D3A36]">
            {ready} ready{generating > 0 ? ` · ${generating} generating` : ""}{failed > 0 ? ` · ${failed} failed` : ""}
          </span>
        )}
      </p>

      {/* Controls */}
      <div className="rounded-xl border border-[#E5E2DC] bg-white p-4 mb-8 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-[#746F66] mb-1">Sex</label>
          <select
            data-testid="select-talent-sex"
            value={sex}
            onChange={(e) => setSex(e.target.value as typeof sex)}
            className="bg-white border border-[#E5E2DC] text-[#3D3A36] text-sm rounded-lg px-3 py-2"
          >
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="mixed">Mixed (50/50)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#746F66] mb-1">Quantity</label>
          <input
            data-testid="input-talent-quantity"
            type="number"
            min={1}
            max={100}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
            className="w-24 bg-white border border-[#E5E2DC] text-[#3D3A36] text-sm rounded-lg px-3 py-2"
          />
        </div>
        <div className="flex-1 min-w-[260px]">
          <label className="block text-xs font-medium text-[#746F66] mb-1">
            Custom influence (optional — biases every prompt)
          </label>
          <input
            data-testid="input-talent-influence"
            value={influence}
            onChange={(e) => setInfluence(e.target.value)}
            maxLength={500}
            placeholder='e.g. "wellness/yoga energy, mostly 25-40" or "gym-bro fitness types"'
            className="w-full bg-white border border-[#E5E2DC] text-[#3D3A36] text-sm rounded-lg px-3 py-2"
          />
        </div>
        <button
          data-testid="button-talent-generate"
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
          className="flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#3D3A36] disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-5 py-2.5"
        >
          {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Generate {quantity}
        </button>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-purple-700" /></div>
      ) : rows.length === 0 && placeholderCount === 0 ? (
        <div className="text-center py-16 text-[#746F66] border border-dashed border-[#E5E2DC] rounded-xl">
          No talent yet — generate your first batch above.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {Array.from({ length: placeholderCount }, (_, i) => (
            <div
              key={`pending-${i}`}
              data-testid={`talent-placeholder-${i}`}
              className="rounded-xl overflow-hidden border border-[#E5E2DC] bg-white"
            >
              <div className="aspect-[9/16] bg-white animate-pulse flex flex-col items-center justify-center gap-2 text-[#746F66] text-xs">
                <Loader2 className="w-5 h-5 animate-spin text-purple-700" /> generating…
              </div>
              <div className="p-2">
                <div className="text-[11px] text-[#746F66] truncate">writing prompt…</div>
                <div className="text-[10px] text-[#746F66] truncate">&nbsp;</div>
              </div>
            </div>
          ))}
          {rows.map((r) => (
            <div key={r.id} data-testid={`talent-card-${r.id}`} className="group relative rounded-xl overflow-hidden border border-[#E5E2DC] bg-white">
              <div className="aspect-[9/16] bg-white flex items-center justify-center">
                {r.status === "ready" && r.cardUrl ? (
                  <img src={r.cardUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                ) : r.status === "generating" ? (
                  <div className="flex flex-col items-center gap-2 text-[#746F66] text-xs">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-700" /> generating…
                  </div>
                ) : (
                  <div className="px-3 text-center text-xs text-red-700">failed{r.error ? `: ${r.error.slice(0, 60)}` : ""}</div>
                )}
              </div>
              <div className="p-2">
                <div className="text-[11px] text-[#3D3A36] truncate">
                  {[r.tags?.ageRange, r.tags?.complexion].filter(Boolean).join(" · ") || r.sex}
                </div>
                <div className="text-[10px] text-[#746F66] truncate">
                  {[r.tags?.hairColor, r.tags?.hairLength, r.tags?.hairTexture, r.tags?.vibe].filter(Boolean).join(" · ")}
                </div>
              </div>
              <button
                data-testid={`button-talent-delete-${r.id}`}
                title="Delete"
                onClick={() => deleteMutation.mutate(r.id)}
                className="absolute top-2 right-2 hidden group-hover:flex items-center justify-center w-7 h-7 rounded-full bg-black/60 hover:bg-red-600/90 text-white"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
